import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [connected, setConnected] = useState(false);
  const [presence, setPresence] = useState({});
  const wsRef = useRef(null);
  const listenersRef = useRef(new Map());
  const reconnectTimeoutRef = useRef(null);

  const connect = useCallback(() => {
    if (!user || !token || wsRef.current?.readyState === WebSocket.OPEN) return;

    // Convert https to wss
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/${user.user_id}?token=${token}`);

    ws.onopen = () => {
      console.log('WebSocket connected');
      setConnected(true);

      // Send heartbeat every 30 seconds
      const heartbeat = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'HEARTBEAT' }));
        }
      }, 30000);

      ws.heartbeatInterval = heartbeat;
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        // Handle presence updates
        if (message.type === 'PRESENCE_UPDATE') {
          setPresence(prev => ({
            ...prev,
            [message.data.user_id]: message.data.status
          }));
        }

        // Notify all listeners
        listenersRef.current.forEach((callback, eventType) => {
          if (eventType === message.type || eventType === '*') {
            callback(message);
          }
        });
      } catch (error) {
        console.error('WebSocket message parse error:', error);
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setConnected(false);
      clearInterval(ws.heartbeatInterval);

      // Attempt to reconnect after 3 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, 3000);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    wsRef.current = ws;
  }, [user, token]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      clearInterval(wsRef.current.heartbeatInterval);
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  const send = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  const subscribeToServer = useCallback((serverId) => {
    send({ type: 'SUBSCRIBE_SERVER', server_id: serverId });
  }, [send]);

  const updatePresence = useCallback((status) => {
    send({ type: 'PRESENCE_UPDATE', status });
  }, [send]);

  const addListener = useCallback((eventType, callback) => {
    const id = `${eventType}_${Date.now()}_${Math.random()}`;
    listenersRef.current.set(id, callback);
    return () => listenersRef.current.delete(id);
  }, []);

  const on = useCallback((eventType, callback) => {
    listenersRef.current.set(eventType, callback);
    return () => listenersRef.current.delete(eventType);
  }, []);

  useEffect(() => {
    if (user && token) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [user, token, connect, disconnect]);

  const value = {
    connected,
    presence,
    send,
    on,
    addListener,
    subscribeToServer,
    updatePresence
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
